import { ModalLayout } from '@/components/HeadlessUI'
import { PopupBody, PopupHeader, PopupWrapper } from '@/components'
import { MODAL_PANEL_CLASS_MD, MODAL_PLACEMENT_CENTER } from '@/constants'
import { LicenseStatusTypes, LicenseTypeTypes, type TenantLicenseDetailResponse } from '@/services/licenseTypes'
import { useState } from 'react'

interface Props {
	license: TenantLicenseDetailResponse | null
	onClose: () => void
}

const statusLabel = (s: LicenseStatusTypes) => {
	switch (s) {
		case LicenseStatusTypes.Active: return 'Active'
		case LicenseStatusTypes.PendingIssue: return 'Pending issue'
		case LicenseStatusTypes.Inactive: return 'Inactive'
		case LicenseStatusTypes.Revoked: return 'Revoked'
		case LicenseStatusTypes.Expired: return 'Expired'
		default: return 'Unknown'
	}
}

const statusClass = (s: LicenseStatusTypes) => {
	if (s === LicenseStatusTypes.Active) return 'text-emerald-500'
	if (s === LicenseStatusTypes.PendingIssue) return 'text-amber-500'
	if (s === LicenseStatusTypes.Revoked || s === LicenseStatusTypes.Expired) return 'text-red-500'
	return 'text-[hsl(var(--muted-foreground))]'
}

const InfoRow = ({ label, value }: { label: string; value?: string | number | null }) => (
	<div className="flex flex-col gap-0.5 sm:flex-row sm:items-start sm:gap-3">
		<span className="w-36 shrink-0 text-xs font-semibold text-[hsl(var(--muted-foreground))]">
			{label}
		</span>
		<span className="text-sm text-[hsl(var(--foreground))]">{value ?? '—'}</span>
	</div>
)

const ViewLicenseModal = ({ license, onClose }: Props) => {
	const [copied, setCopied] = useState(false)

	const copyKey = () => {
		if (!license?.licenseKey) return
		navigator.clipboard.writeText(license.licenseKey).then(() => {
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		})
	}

	return (
		<ModalLayout
			isStatic={false}
			showModal={!!license}
			toggleModal={onClose}
			panelClassName={MODAL_PANEL_CLASS_MD}
			placement={MODAL_PLACEMENT_CENTER}
		>
			{license && (
				<PopupWrapper>
					<PopupHeader title={`License — ${license.tenantName}`} onClose={onClose} />

					<PopupBody>
						<div className="space-y-4">
							{/* Status badge */}
							<div className="flex items-center gap-2">
								<span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${
									license.licenseStatus === LicenseStatusTypes.Active
										? 'bg-emerald-500/10 text-emerald-500 ring-emerald-500/20'
										: license.licenseStatus === LicenseStatusTypes.PendingIssue
										? 'bg-amber-500/10 text-amber-500 ring-amber-500/20'
										: 'bg-red-500/10 text-red-500 ring-red-500/20'
								}`}>
									<span className={`h-1.5 w-1.5 rounded-full ${
										license.licenseStatus === LicenseStatusTypes.Active ? 'bg-emerald-500' :
										license.licenseStatus === LicenseStatusTypes.PendingIssue ? 'bg-amber-500' : 'bg-red-500'
									}`} />
									{statusLabel(license.licenseStatus)}
								</span>
							</div>

							{/* Details grid */}
							<div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/10 p-4 space-y-3">
								<InfoRow label="Tenant" value={license.tenantName} />
								<InfoRow label="Admin" value={`${license.adminDisplayName} (${license.adminEmail})`} />
								<InfoRow label="Plan" value={license.planName} />
								<InfoRow label="License type" value={license.licenseType === LicenseTypeTypes.Trial ? 'Trial' : 'Paid'} />
								{license.licenseStatus === LicenseStatusTypes.Revoked && (
									<InfoRow label="Revoked reason" value={license.revokedReason || 'Not provided'} />
								)}
								<InfoRow label="Starts" value={new Date(license.startedOn).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} />
								<InfoRow label="Expires" value={new Date(license.expiresOn).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} />
								<InfoRow label="Max instances" value={license.maxInstances ?? 'Unlimited'} />
							
							</div>

							{/* License key */}
							{license.licenseKey ? (
								<div>
									<p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
										License Key
									</p>
									<pre className="overflow-auto rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 p-3 text-xs break-all font-mono leading-relaxed">
										{license.licenseKey}
									</pre>
									<button
										type="button"
										className="btn btn-sm btn-ember-outline mt-2 gap-1.5"
										onClick={copyKey}
									>
										<i className={copied ? 'ri-check-line' : 'ri-file-copy-line'} aria-hidden />
										{copied ? 'Copied!' : 'Copy key'}
									</button>
								</div>
							) : (
								<div className="rounded-lg border border-dashed border-[hsl(var(--border))] p-4 text-center text-sm text-[hsl(var(--muted-foreground))]">
									No license key has been issued yet.
								</div>
							)}
						</div>
					</PopupBody>
				</PopupWrapper>
			)}
		</ModalLayout>
	)
}

export default ViewLicenseModal
